import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import {
    ChevronRight, ChevronsLeft, ChevronsRight, Info,
} from 'lucide-react'

import { useEntity } from '@/contexts/EntityContext'
import { useColorScheme } from '@/hooks/useColorScheme'
import { useTenantConfig } from '@/hooks/useTenantConfig'
import {
    mainNavItems,
    settingsNavItems,
    type NavCounterKey,
    type NavItem,
    type NavSubItem,
} from '@/config/navigation'
import { routePrefetch } from '@/App'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarGroup,
    SidebarRail,
    useSidebar,
} from '@/components/ui/sidebar'
import { useCommissionSelfStatus } from '@/hooks/useCommissions'
import { useDiscountApprovals } from '@/hooks/useDiscountApprovals'
import { useExportsUnseenCount } from '@/hooks/useExports'
import { useActionLinksSummary } from '@/hooks/useInstructorActionLinks'
import { usePriceRevisionsCount } from '@/hooks/usePriceRevisions'
import { useTendersToTriageCount } from '@/hooks/useTenders'
import { useWorkflowStats } from '@/hooks/useWorkflows'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { EntitySwitcher } from './EntitySwitcher'
import { APP_VERSION } from '@/version'
import { useChangelogNonLu } from '@/components/changelog/useChangelogNonLu'

/** Où mène la pastille « en échec » : les exécutions ratées, déjà filtrées.
 *
 *  Les trois paramètres sont indissociables. `view=list` quitte la galerie des
 *  modèles, qui n'affiche aucune exécution ; `scope=past` quitte « À venir », qui
 *  ne retient côté serveur que les exécutions planifiées ou suspendues et rendrait
 *  le filtre de statut CONTRADICTOIRE — la liste serait vide et dirait « aucune
 *  exécution », pas « votre filtre s'annule ». Les valeurs de statut sont en
 *  MAJUSCULES : le serveur compare sans normaliser, et `failed` ne matcherait rien,
 *  en silence. */
const ECHECS_HREF = '/automation/workflows?view=list&scope=past&f_status=in:FAILED,PARTIAL'

/** Le libellé du groupe dont un sous-item pointe EXACTEMENT sur ce chemin.
 *
 *  Les DEUX groupes sont balayés — la navigation principale ET le bas de menu
 *  (Paramètres, Aide) : l'accordéon est unique pour toute la barre, sans quoi on
 *  pourrait en tenir un ouvert dans chaque moitié et retrouver le défaut. */
const groupeDuChemin = (pathname: string): string | null => {
    for (const item of [...mainNavItems, ...settingsNavItems]) {
        if (item.children?.some((child) => child.href === pathname)) return item.label
    }
    return null
}

export function AppSidebar() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission, entities, currentEntity } = useEntity()
    const { hasFeature, isFocus, isLoading: configEnChargement } = useTenantConfig()
    // Compte les nouveautés servies à CE tenant depuis sa dernière visite.
    // `useChangelogNonLu` interroge le plan lui-même : on ne lui passe pas de
    // drapeau en prop, sans quoi le geste garderait le comportement et perdrait
    // la raison au prochain découpage.
    const changelogNonLu = useChangelogNonLu()
    const { colorScheme } = useColorScheme()
    const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar()

    const isCollapsed = state === 'collapsed'

    // DOC-003bis Phase 5 — pastille « export prêt non consulté » sur Paramètres.
    // EXP-010 : visible pour TOUT utilisateur authentifié (la page « Exports » liste
    // ses propres exports, RGPD inclus = ouvert à tous), plus seulement les détenteurs
    // de `ged:export`. Le compteur est scopé `created_by` côté serveur.
    const { data: unseenExports = 0 } = useExportsUnseenCount(true)

    // INSTR-016b — l'entrée « Demandes envoyées » n'apparaît qu'une fois la
    // première demande émise (arbitrage PO : ne pas charger le menu des
    // organismes qui n'utiliseront jamais l'invitation), puis reste en place —
    // le compteur porte sur TOUS les statuts, donc l'historique reste joignable.
    // La pastille, elle, ne compte que ce qui attend une action.
    const { data: requestsSummary } = useActionLinksSummary(
        currentEntity?.entityId ?? '',
        hasPermission('instructors:read'),
    )
    // SALE-025 §4 — les dérogations EN ATTENTE, et elles seules : c'est ce qui
    // bloque un devis, donc ce qui mérite une pastille. Le calcul est déjà servi
    // par la file de l'approbateur ; on ne crée pas d'endpoint de comptage.
    const { data: approvalQueue } = useDiscountApprovals(
        currentEntity?.entityId ?? '',
        { status: 'PENDING' },
        { enabled: hasPermission('discount:approve') },
    )
    // SALE-026 — « Commissions » n'apparaît qu'avec un barème en place. Ce n'est
    // pas un COMPTEUR mais un booléen : on le porte dans la même mécanique
    // (0 = masqué) plutôt que d'inventer un second concept de visibilité.
    const { data: selfCommission } = useCommissionSelfStatus(
        currentEntity?.entityId ?? '',
        hasPermission('commissions:view_own'),
    )
    // WKF-008 §6.1 — automatisations en ÉCHEC sur 7 jours glissants. Une
    // automatisation qui échoue est SILENCIEUSE par nature : personne n'ouvre un
    // historique pour vérifier qu'un e-mail est bien parti. Sans cette pastille, il
    // fallait qu'un client se plaigne de ne rien avoir reçu pour qu'on découvre la
    // panne.
    //
    // Fenêtre GLISSANTE plutôt que « non consulté » : la pastille retombe d'elle-même
    // quand les échecs cessent, sans marqueur de lecture à écrire en base — et une
    // pastille qui ne s'éteint jamais finit par ne plus rien vouloir dire.
    // `errors_7d` compte les exécutions FAILED **et** PARTIAL : dans les deux cas,
    // quelque chose que l'organisme attendait n'est pas parti.
    const { data: workflowStats } = useWorkflowStats(
        hasPermission('workflows:view_runs') && hasFeature('WORKFLOWS')
            ? (currentEntity?.entityId ?? '')
            : '',
    )
    /*
     * Les appels d'offres À TRIER — les fiches au stage `VEILLE`, qu'elles
     * viennent du BOAMP ou d'une saisie. Sans pastille, une pile qu'on doit
     * penser à ouvrir ne s'ouvre pas. Gardée par le droit ET le plan, comme ses
     * voisines — sans quoi un FOCUS paierait une requête pour un menu qu'il n'a
     * pas.
     *
     * ⚠️ Elle ne retombe pas quand on REGARDE, seulement quand on DÉCIDE :
     * qualifier la fiche ou l'abandonner avec un motif. C'est ce qui la rend
     * lisible comme une charge de travail plutôt que comme un accusé de lecture.
     */
    const { data: veille } = useTendersToTriageCount(
        hasPermission('tenders:read') && hasFeature('TENDERS')
            ? (currentEntity?.entityId ?? '')
            : '',
    )
    // TND-013 — « Révisions de prix » n'apparaît qu'avec au moins un contrat cadre.
    // Gatée comme les contrats cadre (masqués en FOCUS) ; aucune requête tant que
    // le plan n'est pas connu — le repli est « masqué ».
    //
    // ⚠️ On demande le COMPTE, pas la liste. Cette barre est montée sur CHAQUE
    // écran et rafraîchit toutes les 60 s : depuis que la vue sert aussi le
    // secteur privé, lui servir la liste faisait transiter tout le portefeuille
    // de contrats cadres en permanence (~1 Ko par contrat). Le compte partage le
    // `FROM … WHERE` de la liste côté serveur, donc le menu ne peut pas se
    // contredire avec la page — c'est là, et non ici, que l'invariant est tenu.
    const { data: revisionsDePrix } = usePriceRevisionsCount(currentEntity?.entityId ?? '', {
        enabled: hasPermission('clients:read') && !configEnChargement && !isFocus,
    })
    const navCounters: Record<NavCounterKey, number> = {
        instructorRequests: requestsSummary?.total ?? 0,
        pendingApprovals: approvalQueue?.length ?? 0,
        ownCommissions: selfCommission?.has_rule ? 1 : 0,
        // ⚠️ La pastille ne compte QUE les exécutions en échec — plus les cibles
        // d'exception orphelines, qu'elle additionnait sous le même libellé.
        //
        // Les deux natures existent toutes les deux, mais elles ne se montrent pas
        // au même endroit : une exécution en échec s'ouvre dans la liste des
        // exécutions, une cible partie ne produit AUCUNE exécution et n'y apparaîtra
        // jamais. « 5 en échec » qui n'ouvre que 3 lignes n'est pas un arrondi, c'est
        // une pastille qui montre moins que ce qu'elle annonce — et le schéma du
        // serveur disait déjà, en commentaire, pourquoi il ne fallait pas les
        // additionner. Les cibles orphelines se signalent désormais sur la LIGNE du
        // modèle concerné, là où le geste de réparation se trouve.
        workflowErrors: workflowStats?.errors_7d ?? 0,
        tendersToTriage: veille?.to_triage_count ?? 0,
        priceRevisions: revisionsDePrix?.total ?? 0,
    }
    const pendingRequests = requestsSummary?.submitted ?? 0

    // Header background: neutral uses dark gray, Argalis uses sidebar-primary (CSS variable)
    const headerBg = colorScheme === 'neutral'
        ? 'bg-[#212328] dark:bg-[#323641]'
        : 'bg-sidebar-primary'

    const isActive = (href: string) => location.pathname === href

    /* ACCORDÉON — un seul sous-menu déplié à la fois.
     *
     * Chaque groupe était un `Collapsible` INDÉPENDANT (`defaultOpen`) : rien
     * n'empêchait de les ouvrir tous, et la barre latérale devenait alors plus
     * haute que l'écran — il fallait scroller pour atteindre une entrée, puis
     * refermer un à un pour retrouver la vue d'ensemble. Un état UNIQUE ferme
     * le précédent en ouvrant le suivant, et la barre garde sa hauteur.
     *
     * Il s'initialise — et se ré-ouvre à chaque navigation — sur le groupe qui
     * contient la page courante : arriver sur un écran par un lien, la
     * recherche ⌘K ou un rechargement montre où l'on est.
     *
     * ⚠️ On ne referme JAMAIS sur une route qu'aucun groupe ne revendique
     * (fiche de détail, entrée sans sous-menu) : sinon le menu se refermerait
     * sous le doigt de qui vient d'ouvrir un groupe pour explorer : « aucun
     * groupe ne correspond » n'est pas « aucun groupe ne doit rester ouvert ».
     *
     * L'initialiseur de `useState` fait DOUBLON avec l'effet, qui joue lui aussi
     * au montage — mais un rendu trop tard : le menu se peindrait fermé, puis
     * s'ouvrirait en animant. Aucun test ne peut les distinguer (le DOM final est
     * le même), et le sabotage de l'initialiseur reste vert : c'est le seul point
     * de ce bloc dont la raison est purement visuelle.
     */
    const [openGroup, setOpenGroup] = useState<string | null>(
        () => groupeDuChemin(location.pathname),
    )
    useEffect(() => {
        const groupe = groupeDuChemin(location.pathname)
        if (groupe) setOpenGroup(groupe)
    }, [location.pathname])

    // Accès = permission RBAC ET feature de plan (INSTR-017 §4, doctrine
    // masqué-pas-grisé : une entrée hors plan est invisible, pas grisée).
    const canAccess = (permission?: string, feature?: string) => {
        if (permission && !hasPermission(permission)) return false
        if (feature && !hasFeature(feature)) return false
        return true
    }

    /** Entrée à compteur : visible seulement si la fonction a déjà servi. */
    const isSubItemVisible = (child: NavSubItem) => {
        if (!canAccess(child.permission, child.feature)) return false
        // Une entrée qui mène à plusieurs onglets gardés séparément : il suffit
        // d'en pouvoir ouvrir UN. Sans cela, l'écran existe et le menu n'y mène
        // pas (constaté sur « Suivi & arbitrage », §13.35).
        if (child.anyPermission && !child.anyPermission.some(hasPermission)) return false
        if (child.hideWhenEmpty && navCounters[child.hideWhenEmpty] === 0) return false
        return true
    }

    const renderNavItem = (item: NavItem) => {
        // Special case for home (/): exact match only, otherwise it matches all paths
        const isActiveItem = item.href
            ? (item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href))
            : false
        const submenu = item.children?.filter(isSubItemVisible)
        const hasSubmenu = submenu && submenu.length > 0

        // Hide parent if it has children but none are accessible
        if (item.children && !hasSubmenu) return null
        // Hide simple items the user cannot access
        if (!item.children && !canAccess(item.permission, item.feature)) return null

        // CASE 1: COLLAPSED + SUBMENU
        if (isCollapsed && hasSubmenu) {
            return (
                <SidebarMenuItem key={item.label}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuButton
                                isActive={isActiveItem}
                                tooltip={t(item.label)}
                                className="w-full justify-center rounded-xl"
                            >
                                <item.icon className="h-5 w-5" />
                                <span className="sr-only">{t(item.label)}</span>
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start" className="min-w-56 rounded-xl">
                            <DropdownMenuLabel className="bg-muted/40 -mx-1 -mt-1 px-3 py-2 rounded-t-xl border-b mb-1">
                                {t(item.label)}
                            </DropdownMenuLabel>
                            {submenu!.map((sub) => {
                                return (
                                    <DropdownMenuItem
                                        key={sub.href}
                                        onClick={() => navigate(sub.href || '#')}
                                        onMouseEnter={() => sub.href && routePrefetch[sub.href]?.()}
                                        className="gap-2 rounded-lg focus:bg-sidebar-accent focus:text-sidebar-accent-foreground"
                                    >
                                        {t(sub.label)}
                                    </DropdownMenuItem>
                                )
                            })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarMenuItem>
            )
        }

        // CASE 2: EXPANDED + SUBMENU
        if (!isCollapsed && hasSubmenu) {
            return (
                <Collapsible
                    key={item.label}
                    asChild
                    open={openGroup === item.label}
                    onOpenChange={(ouvert) => setOpenGroup(ouvert ? item.label : null)}
                    className="group/collapsible"
                >
                    <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                                tooltip={t(item.label)}
                                className="w-full justify-between rounded-xl"
                            >
                                <div className="flex items-center gap-2">
                                    <item.icon className="h-4 w-4" />
                                    <span>{t(item.label)}</span>
                                </div>
                                <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="animate-collapsible-down overflow-hidden data-[state=closed]:animate-collapsible-up">
                            <SidebarMenuSub>
                                {submenu!.map((sub) => {
                                    return (
                                    <SidebarMenuSubItem key={sub.href}>
                                        <SidebarMenuButton
                                            asChild
                                            tooltip={t(sub.label)}
                                            isActive={isActive(sub.href || '')}
                                            className={`transition-all duration-200 rounded-xl ${isActive(sub.href || '') ? 'font-bold' : ''}`}
                                        >
                                            <Link
                                                to={sub.href || '#'}
                                                className="text-xs"
                                                onClick={() => { if (isMobile) setOpenMobile(false) }}
                                                onMouseEnter={() => sub.href && routePrefetch[sub.href]?.()}
                                            >
                                                <span>{t(sub.label)}</span>
                                                {/* Pastille « dossiers à valider » (INSTR-016b) :
                                                    un dépôt qui arrive doit se voir depuis le menu,
                                                    sinon il dort. */}
                                                {sub.counter && navCounters[sub.counter] > 0 ? (
                                                    <span
                                                        className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
                                                        aria-label={t('sales.pilotage.pendingAria', {
                                                            count: navCounters[sub.counter],
                                                            defaultValue: '{{count}} dérogation(s) en attente',
                                                        })}
                                                    >
                                                        {navCounters[sub.counter]}
                                                    </span>
                                                ) : null}
                                                {sub.hideWhenEmpty === 'instructorRequests' &&
                                                pendingRequests > 0 ? (
                                                    <span
                                                        className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
                                                        aria-label={t('instructors.requests.pendingAria', {
                                                            count: pendingRequests,
                                                        })}
                                                    >
                                                        {pendingRequests}
                                                    </span>
                                                ) : null}
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuSubItem>
                                    )
                                })}
                            </SidebarMenuSub>
                        </CollapsibleContent>
                    </SidebarMenuItem>
                </Collapsible>
            )
        }

        // CASE 3: SIMPLE ITEM (No Submenu)
        // Pastille « export prêt non consulté » sur Paramètres (DOC-003bis Phase 5).
        const showExportBadge = item.href === '/settings' && unseenExports > 0
        // Pastille « automatisations en échec » (WKF-008 §6.1) — même patron que
        // celle des exports, en teinte DESTRUCTIVE : ce n'est pas une bonne
        // nouvelle qui attend, c'est quelque chose qui n'est pas parti.
        const itemCounter = item.counter ? navCounters[item.counter] : 0
        return (
            <SidebarMenuItem key={item.label}>
                <SidebarMenuButton
                    asChild
                    isActive={isActiveItem}
                    tooltip={
                        showExportBadge
                            ? `${t(item.label)} — ${t('exports.nav_unseen', { count: unseenExports })}`
                            : t(item.label)
                    }
                    className={`rounded-xl ${isActiveItem ? 'font-bold' : ''}`}
                    onClick={() => { if (item.href) navigate(item.href); if (isMobile) setOpenMobile(false) }}
                >
                    <button className="w-full text-left">
                        <item.icon className={isCollapsed ? "h-5 w-5" : "h-4 w-4"} />
                        <span>{t(item.label)}</span>
                    </button>
                </SidebarMenuButton>
                {showExportBadge && (
                    <>
                        {/* Mode ÉTENDU : chip texte explicite (vert doux du DS). */}
                        <span
                            aria-label={t('exports.nav_unseen', { count: unseenExports })}
                            className="pointer-events-none absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center whitespace-nowrap rounded-full bg-success-bg px-2 py-0.5 text-[11px] font-semibold leading-none text-success group-data-[collapsible=icon]:hidden"
                        >
                            {t('exports.nav_chip', { count: unseenExports })}
                        </span>
                        {/* Mode REPLIÉ : compteur en pastille sur le coin de l'icône. */}
                        <span
                            aria-label={t('exports.nav_unseen', { count: unseenExports })}
                            className="pointer-events-none absolute right-0.5 top-0.5 z-10 hidden h-4 min-w-4 items-center justify-center rounded-full bg-success-bg px-1 text-[10px] font-bold leading-none text-success ring-2 ring-sidebar group-data-[collapsible=icon]:flex"
                        >
                            {unseenExports}
                        </span>
                    </>
                )}
                {itemCounter > 0 && (
                    <>
                        {/* ⚠️ La pastille est un LIEN, pas un décor — et c'est le fond
                            du correctif. L'entrée de menu mène à la galerie des
                            modèles, un écran qui n'affiche AUCUNE exécution : il
                            fallait deux bascules et un filtre pour atteindre ce que
                            la pastille annonçait. Le lien porte donc les trois d'un
                            coup : la vue, le périmètre (« À venir » exclut les échecs
                            par construction) et le filtre de statut.

                            Elle est un FRÈRE du bouton de navigation dans le `<li>`,
                            jamais un enfant : aucun interactif imbriqué, et le clic
                            ne remonte pas au bouton. Cliquer la ligne ouvre les
                            modèles, cliquer la pastille ouvre les échecs — chaque
                            geste mène à son propre sujet.

                            Les DEUX modes reçoivent le lien : n'en traiter qu'un
                            donnerait une pastille cliquable en menu déplié et inerte
                            en menu replié. */}
                        <Link
                            to={ECHECS_HREF}
                            aria-label={t('workflows.nav.errorsAria', { count: itemCounter })}
                            onClick={() => { if (isMobile) setOpenMobile(false) }}
                            className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center whitespace-nowrap rounded-full bg-destructive-bg px-2 py-0.5 text-[11px] font-semibold leading-none text-destructive transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-data-[collapsible=icon]:hidden"
                        >
                            {t('workflows.nav.errorsChip', { count: itemCounter })}
                        </Link>
                        <Link
                            to={ECHECS_HREF}
                            aria-label={t('workflows.nav.errorsAria', { count: itemCounter })}
                            onClick={() => { if (isMobile) setOpenMobile(false) }}
                            className="absolute right-0.5 top-0.5 z-10 hidden h-4 min-w-4 items-center justify-center rounded-full bg-destructive-bg px-1 text-[10px] font-bold leading-none text-destructive ring-2 ring-sidebar transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-data-[collapsible=icon]:flex"
                        >
                            {itemCounter}
                        </Link>
                    </>
                )}
            </SidebarMenuItem>
        )
    }

    return (
        <Sidebar collapsible="icon">
            {/* Header: Dark teal background with logo */}
            <SidebarHeader className="p-0 gap-0">
                {/* Safe area spacer on mobile — same bg as logo header to avoid color mismatch */}
                {isMobile && <div className={`${headerBg} h-[env(safe-area-inset-top)]`} />}
                <div className={`flex items-center justify-center ${headerBg} h-14`}>
                    {isCollapsed ? (
                        <img
                            src="/images/logo-argalis-icon.png"
                            alt="Argalis"
                            className="h-7 w-7 sidebar-logo"
                        />
                    ) : (
                        <img
                            src="/images/logo-argalis-full-white.png"
                            alt="Argalis"
                            className="h-8 sidebar-logo"
                        />
                    )}
                </div>

                {/* Entity Switcher - Only visible if multiple entities */}
                {entities.length > 1 && (
                    <div className="p-2">
                        <EntitySwitcher />
                    </div>
                )}
            </SidebarHeader>

            {/* Main Navigation */}
            <SidebarContent>
                <SidebarGroup>
                    <SidebarMenu>
                        {mainNavItems.map(renderNavItem)}
                    </SidebarMenu>
                </SidebarGroup>

                {/* Secondary Nav (Settings & Help) - Pushed to bottom of content area */}
                <SidebarGroup className="mt-auto">
                    <SidebarMenu>
                        {settingsNavItems.map(renderNavItem)}
                    </SidebarMenu>
                </SidebarGroup>
            </SidebarContent>

            {/* Footer with Version & Collapse */}
            <SidebarFooter className="p-2">
                {/* Version Card - Compact & Theme aligned */}
                <SidebarMenuItem>
                    <SidebarMenuButton
                        asChild
                        tooltip={t('changelog.title')}
                        className={`h-auto w-full py-1 px-1.5 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center border-none bg-transparent' : 'justify-start border border-sidebar-border/50 bg-sidebar-card'}`}
                        onClick={() => { navigate('/changelog'); if (isMobile) setOpenMobile(false) }}
                    >
                        <button className="flex items-center gap-2">
                            <div className="relative flex h-4 w-4 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
                                <Info className="h-2.5 w-2.5" />
                                {/* Replié, le compte ne tient pas : on garde le point seul. */}
                                {changelogNonLu > 0 && isCollapsed && (
                                    <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                                )}
                            </div>
                            {!isCollapsed && (
                                <>
                                    <span className="truncate text-[10px] font-medium text-muted-foreground">v{APP_VERSION}</span>
                                    {changelogNonLu > 0 && (
                                        <span className="ml-auto shrink-0 rounded-full bg-primary px-1.5 text-[9px] font-semibold leading-4 text-primary-foreground">
                                            {changelogNonLu > 9 ? '9+' : changelogNonLu}
                                        </span>
                                    )}
                                </>
                            )}
                        </button>
                    </SidebarMenuButton>
                </SidebarMenuItem>

                {/* Collapse Trigger */}
                <button
                    onClick={toggleSidebar}
                    className={`mt-2 flex w-full items-center rounded-xl p-2 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground ${isCollapsed ? 'justify-center' : 'justify-start gap-2'
                        }`}
                >
                    {isCollapsed ? (
                        <ChevronsRight className="h-5 w-5" />
                    ) : (
                        <ChevronsLeft className="h-4 w-4" />
                    )}
                    {!isCollapsed && <span className="text-xs font-medium">{t('common.close')}</span>}
                </button>
            </SidebarFooter>

            {/* Rail for edge toggle (click on edge) */}
            <SidebarRail />
        </Sidebar>
    )
}
